/*  Shared  */
import { useQueryClient } from '@tanstack/react-query';
import { Dashboard } from 'application/Shared/Components/Dashboard';

/*  Types  */
import { Dashboard as DashboardType } from 'application/Shared/Components/Dashboard/types';
import { ViewScheduledReportSetup } from 'application/Shared/Modals/ViewScheduledReportSetup';
import { ScheduledReport as ScheduledReportType } from 'application/Shared/types/scheduledReport';

/*  React  */
import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';

/*  Data  */
import scheduledReportsDashboardAdmin from './internal/scheduledReportsAdmin.json';
import scheduledReportsDashboardPartner from './internal/scheduledReportsPartner.json';

export const ScheduledReports = () => {
    const { clientId } = useParams();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();

    // Grab the query keys related to scheduled reports
    const queryCache = queryClient.getQueryCache();
    const liveQueriesOnScreen = queryCache.findAll();
    const queryKeys = liveQueriesOnScreen.map((query) => query.queryKey);

    const adminScheduledReportsQueryKey = queryKeys.filter((queryKey) =>
        queryKey?.includes('admin-scheduled-reports')
    );
    const partnerScheduledReportsQueryKey = queryKeys.filter((queryKey) =>
        queryKey?.includes('partner-scheduled-reports')
    );

    const scheduledReportId = searchParams.get('scheduledReportId');

    useEffect(() => {
        const queryKey = clientId
            ? adminScheduledReportsQueryKey[0]
            : partnerScheduledReportsQueryKey[0];

        // Need to invalidate queries because when we delete a scheduled report we need fresh data.
        if (queryKey) {
            queryClient.invalidateQueries({ exact: true, queryKey });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, queryClient]);

    // Grabbing the data of all the scheduled reports from the cached data.
    const scheduledReportsQueryData = queryClient.getQueryData(
        clientId
            ? adminScheduledReportsQueryKey[0]
            : partnerScheduledReportsQueryKey[0]
    );

    // Filter for a particular scheduled report
    const scheduledReportData = (
        scheduledReportsQueryData as ScheduledReportType[]
    )?.find((data) => data.id === scheduledReportId);

    // After closing the flyout for a particular scheduled report, we need to wipe the scheduledReportId
    const handleRemoveParam = () => {
        setSearchParams((currentParams) => {
            if (scheduledReportId) {
                currentParams.delete('scheduledReportId');
            }
            return new URLSearchParams(currentParams);
        });
    };

    return (
        <>
            <Dashboard
                dashboard={
                    clientId
                        ? (scheduledReportsDashboardAdmin as DashboardType)
                        : (scheduledReportsDashboardPartner as DashboardType)
                }
            />
            {scheduledReportData && (
                <ViewScheduledReportSetup
                    scheduledReport={scheduledReportData}
                    onClose={handleRemoveParam}
                />
            )}
        </>
    );
};
